'use client';

import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Minus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@supabase/supabase-js';
import Link from 'next/link';
import { getTranslations } from '@/lib/i18n';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

interface GuestEntry {
  name: string;
  dietary: string;
  dietaryOther: string;
}

interface RSVPFormValues {
  attending: 'yes' | 'no' | '';
  primaryName: string;
  primaryEmail: string;
  primaryDietary: string;
  primaryDietaryOther: string;
  message: string;
  plusOnes: GuestEntry[];
  fridayEvening: boolean;
  sundayBrunch: boolean;
  beachSauna: boolean;
}

const dietaryOptionsFr = [
  { value: 'none', label: 'Aucune exigence' },
  { value: 'nut-allergy', label: 'Allergie aux noix' },
  { value: 'gluten-free', label: 'Sans gluten' },
  { value: 'other', label: 'Autre (veuillez mentionner dans le message)' },
];

export default function FrRSVPForm() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const t = getTranslations('fr');

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<RSVPFormValues>({
    defaultValues: {
      attending: '',
      primaryName: '',
      primaryEmail: '',
      primaryDietary: 'none',
      primaryDietaryOther: '',
      message: '',
      plusOnes: [],
      fridayEvening: false,
      sundayBrunch: false,
      beachSauna: false,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'plusOnes' });
  const attending = watch('attending');
  const primaryDietary = watch('primaryDietary');

  const onSubmit = async (data: RSVPFormValues) => {
    setIsLoading(true);
    try {
      const { error: dbError } = await supabase.from('rsvp_submissions').insert({
        attending: data.attending,
        primary_name: data.primaryName,
        primary_email: data.primaryEmail || null,
        primary_dietary: data.primaryDietary,
        message: data.message || null,
        plus_ones: data.plusOnes,
        friday_evening: data.fridayEvening,
        sunday_brunch: data.sundayBrunch,
        sunday_walk: data.beachSauna,
      });

      if (dbError) throw new Error(dbError.message);

      if (data.primaryEmail) {
        await supabase.functions.invoke('send-rsvp-confirmation', {
          body: {
            attending: data.attending,
            primaryName: data.primaryName,
            primaryEmail: data.primaryEmail,
            primaryDietary: data.primaryDietary,
            plusOnes: data.plusOnes,
            fridayEvening: data.fridayEvening,
            sundayBrunch: data.sundayBrunch,
            sundayWalk: data.beachSauna,
            message: data.message,
          },
        });
      }

      toast.success('Votre RSVP a bien été reçu. Merci.');
      setSubmitted(true);
    } catch (err) {
      console.error('RSVP submission error:', err);
      toast.error('Une erreur s\'est produite. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-12 pb-28 px-8 md:px-16 xl:px-24">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle size={40} className="text-olive mx-auto mb-8" strokeWidth={1} />
          <h2 className="font-serif text-display-md text-faded-navy mb-6">
            {attending === 'yes' ? 'Nous avons hâte de célébrer avec vous.' : 'Merci de nous avoir prévenus.'}
          </h2>
          <div className="h-px bg-border w-16 mx-auto mb-6" />
          <p className="font-sans text-sm text-stone leading-relaxed">
            {attending === 'yes' ?'Votre RSVP a bien été reçu. Nous vous contacterons avec plus de détails à l\'approche du jour. En attendant, explorez Dartmouth ci-dessous.' :'Nous sommes désolés que vous ne puissiez pas nous rejoindre. Vous serez dans nos pensées ce jour-là.'}
          </p>
          <div className="mt-8">
            <Link href="/fr/dartmouth" className="inline-block font-sans text-sm text-olive underline underline-offset-4 hover:text-faded-navy transition-colors">
              Hébergement &amp; Activités
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-12 pb-24 px-8 md:px-16 xl:px-24">
      <div className="max-w-2xl mx-auto">
        <div className="mb-16">
          <p className="section-label mb-4">{t.rsvp.formLabel}</p>
          <h2 className="font-serif text-display-md text-faded-navy mb-6">{t.rsvp.formTitle}</h2>
          <p className="font-sans text-sm text-stone leading-relaxed">{t.rsvp.formSubtitle}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-14">
          {/* Attendance */}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-6">{t.rsvp.attendance}</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'yes', label: t.rsvp.joyfullyAccepts, sublabel: t.rsvp.willBeThere },
                { value: 'no', label: t.rsvp.regretfullyDeclines, sublabel: t.rsvp.cannotAttend },
              ].map((option) => (
                <label
                  key={`attend-${option.value}`}
                  className={`relative flex flex-col items-center justify-center p-6 border cursor-pointer transition-all duration-300 ${
                    attending === option.value ? 'border-faded-navy bg-off-white' : 'border-border hover:border-warm-sand'
                  }`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    className="sr-only"
                    {...register('attending', { required: t.rsvp.attendanceRequired })}
                  />
                  <span className="font-serif text-lg text-faded-navy mb-1">{option.label}</span>
                  <span className="text-xs font-sans text-stone">{option.sublabel}</span>
                  {attending === option.value && (
                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-faded-navy" />
                  )}
                </label>
              ))}
            </div>
            {errors.attending && (
              <p className="mt-3 text-xs font-sans text-red-500">{errors.attending.message}</p>
            )}
          </div>

          {attending && (
            <>
              {/* Name */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                  Votre nom <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors"
                  placeholder="Prénom et nom"
                  {...register('primaryName', { required: 'Veuillez entrer votre nom' })}
                />
                {errors.primaryName && (
                  <p className="mt-2 text-xs font-sans text-red-500">{errors.primaryName.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                  Adresse e-mail <span className="text-stone/40">(facultatif)</span>
                </label>
                <input
                  type="email"
                  className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors"
                  placeholder="votre@email.com"
                  {...register('primaryEmail')}
                />
              </div>

              {/* Dietary */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                  Régime alimentaire
                </label>
                <div className="relative">
                  <select
                    className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy focus:outline-none focus:border-faded-navy transition-colors appearance-none"
                    {...register('primaryDietary')}
                  >
                    {dietaryOptionsFr.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {attending === 'yes' && (
                <>
                  {/* Weekend events */}
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-6">Événements du week-end</p>
                    <div className="space-y-4">
                      {[
                        { name: 'fridayEvening' as const, label: 'Rassemblement du vendredi soir', sublabel: 'Galions Quay · 17h00–19h30 · Facultatif' },
                        { name: 'sundayBrunch' as const, label: 'Matinée du dimanche à la plage', sublabel: 'Blackpool Sands · À partir de 10h00' },
                        { name: 'beachSauna' as const, label: 'Sauna de plage', sublabel: 'Blackpool Sands · Dimanche matin' },
                      ].map((item) => (
                        <label key={item.name} className="flex items-start gap-4 cursor-pointer group">
                          <div className="relative mt-0.5">
                            <input type="checkbox" className="sr-only" {...register(item.name)} />
                            <div className={`w-5 h-5 border transition-colors duration-200 flex items-center justify-center ${watch(item.name) ? 'border-faded-navy bg-faded-navy' : 'border-border group-hover:border-warm-sand'}`}>
                              {watch(item.name) && <div className="w-2 h-2 bg-off-white" />}
                            </div>
                          </div>
                          <div>
                            <p className="font-sans text-sm text-faded-navy">{item.label}</p>
                            <p className="font-sans text-xs text-stone/60 mt-0.5">{item.sublabel}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Plus ones */}
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-6">Accompagnants</p>
                    {fields.map((field, index) => (
                      <div key={field.id} className="mb-6 p-5 border border-border">
                        <div className="flex items-center justify-between mb-4">
                          <p className="font-sans text-xs text-stone">Invité {index + 1}</p>
                          <button type="button" onClick={() => remove(index)} className="text-stone/40 hover:text-stone transition-colors">
                            <Minus size={14} />
                          </button>
                        </div>
                        <div className="space-y-4">
                          <input
                            type="text"
                            className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy"
                            placeholder="Prénom et nom"
                            {...register(`plusOnes.${index}.name`)}
                          />
                          <select
                            className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy focus:outline-none focus:border-faded-navy appearance-none"
                            {...register(`plusOnes.${index}.dietary`)}
                          >
                            {dietaryOptionsFr.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => append({ name: '', dietary: 'none', dietaryOther: '' })}
                      className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-sans text-stone hover:text-faded-navy transition-colors"
                    >
                      <Plus size={14} />
                      Ajouter un accompagnant
                    </button>
                  </div>
                </>
              )}

              {/* Message */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                  Message <span className="text-stone/40">(facultatif)</span>
                </label>
                <textarea
                  rows={4}
                  className="w-full border border-border bg-transparent px-4 py-3 font-sans text-sm text-faded-navy placeholder-stone/40 focus:outline-none focus:border-faded-navy transition-colors resize-none"
                  placeholder="Exigences alimentaires, questions ou un mot pour nous..."
                  {...register('message')}
                />
              </div>

              {/* Submit */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Envoi en cours…' : 'Envoyer mon RSVP'}
                </button>
                <p className="mt-6 text-center font-sans text-xs text-stone/60">
                  {t.rsvp.alreadySubmitted}{' '}
                  <Link href="/guest-dashboard" className="text-faded-navy underline underline-offset-2 hover:opacity-70 transition-opacity">
                    {t.rsvp.enterDashboard}
                  </Link>
                </p>
              </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
}
